import React, { useCallback, useMemo, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { Icon } from 'tea-component';

import { DEFAULT_CLASS_NAMES, DEFAULT_CONFIG } from '@/pages/datalab/components/QuickDragList/constants';
import { getMoveDecision } from './moveDecision';
import {
  ITwoDragItemBase,
  ITwoDragListProps,
  TTwoDragSide,
} from './types';
import { useTwoDragSession } from './useTwoDragSession';

import SvgIcon from '@home/components/SvgIcon';
import './index.scss';

const DRAG_NOT_ALLOWED_CURSOR_CLASS = 'dbl-two-drag-list-wrapper--drag-not-allowed';

/**
 * TwoDragList：上下双框 + 双向拖拽的通用受控容器。
 * - 内部仅提供方向/同框排序/非法回退的基础策略能力
 * - 具体业务规则由调用方通过 onMove/onDropReject/onChange 编排
 */
function TwoDragListBase<TItem extends ITwoDragItemBase>(
  props: ITwoDragListProps<TItem>,
): React.ReactElement {
  // ===== 1) Props 解构 =====
  const {
    topItems,
    bottomItems,
    onChange,
    topTitle,
    bottomTitle,
    maxHeight,
    direction = 'bidirectional',
    topSortable = true,
    bottomSortable = true,
    onDropReject,
    renderItem,
    containerClassName,
    containerStyle,
    topBoxClassName,
    topBoxStyle,
    bottomBoxClassName,
    bottomBoxStyle,
    chosenClass = DEFAULT_CLASS_NAMES.chosen,
    dragClass = DEFAULT_CLASS_NAMES.drag,
    ghostClass = DEFAULT_CLASS_NAMES.ghost,
    fallbackClass,
    animation = DEFAULT_CONFIG.animation,
    delay,
    handle,
    filter,
    disabled,
    onMove,
  } = props;

  // 拖拽禁源项的 filter selector（用于 ReactSortable filter）。
  const disabledItemClassName = 'dbl-two-drag-list-item--drag-disabled';

  // ===== 2) 拖拽会话：受控 onChange 聚合与去重 =====
  const {
    handleDragStart,
    handleDragEnd,
    handleTopSetList,
    handleBottomSetList,
  } = useTwoDragSession(topItems, bottomItems, onChange);
  const [isDragForbidden, setIsDragForbidden] = useState<boolean>(false);
  const setDragForbiddenCursor = useCallback((isForbidden: boolean): void => {
    setIsDragForbidden(isForbidden);
  }, []);

  // ===== 3) 准入过滤：把 dragDisabled 追加到外部 filter 末尾 =====
  const mergedFilter = useMemo(() => {
    const disabledFilter = `.${disabledItemClassName}`;
    if (!filter) return disabledFilter;

    // 允许调用方继续传入其他 filter 选择器；此处将禁拖 selector 追加到末尾。
    return `${filter},${disabledFilter}`;
  }, [filter]);

  // ===== 4) 布局样式：maxHeight =====
  const maxHeightStyle = useMemo<React.CSSProperties>(() => {
    if (maxHeight === undefined) return {};

    return {
      maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
    };
  }, [maxHeight]);

  // ===== 5) 拖拽准入：onMove 决策器 =====
  const getSideFromSortableElement = useCallback(
    (el: HTMLElement | undefined | null): TTwoDragSide | null => {
      if (!el) return null;
      const sideContainer = el.closest('[data-two-drag-side]');
      if (!(sideContainer instanceof HTMLElement)) return null;
      const side = sideContainer.getAttribute('data-two-drag-side');
      if (side === 'top' || side === 'bottom') return side;
      return null;
    },
    [],
  );

  /**
   * 拖拽准入/回退决策器（onMove）：
   * - react-sortablejs 在受控模式下会根据 onMove 返回值阻止移动并回退数据状态。
   * - 内部规则校验失败时：return false，并触发 onDropReject（如存在）。
   * - 内部规则通过后：若外部 onMove 返回 false，同样 return false + 触发 onDropReject。
   */
  const handleMove = useCallback(
    (evt: any, originalEvent: any): boolean => {
      const fromSide = getSideFromSortableElement(evt?.from);
      const toSide = getSideFromSortableElement(evt?.to);
      if (!fromSide || !toSide) {
        setDragForbiddenCursor(true);
        onDropReject?.({
          actionType: 'move',
          from: fromSide ?? 'top',
          to: toSide ?? 'bottom',
          reason: 'unknown_move_context',
        });

        return false;
      }

      const decision = getMoveDecision({
        from: fromSide,
        to: toSide,
        direction,
        topSortable,
        bottomSortable,
      });

      if (!decision.allowed) {
        setDragForbiddenCursor(true);
        onDropReject?.({
          actionType: decision.actionType,
          from: fromSide,
          to: toSide,
          reason: decision.reason,
        });

        return false;
      }

      const externalResult = onMove?.(evt, originalEvent);
      if (externalResult === false) {
        setDragForbiddenCursor(true);
        onDropReject?.({
          actionType: 'move',
          from: fromSide,
          to: toSide,
          reason: 'external_onMove_rejected',
        });

        return false;
      }

      setDragForbiddenCursor(false);
      return true;
    },
    [
      bottomSortable,
      direction,
      getSideFromSortableElement,
      onDropReject,
      onMove,
      setDragForbiddenCursor,
      topSortable,
    ],
  );

  const handleSortableStart = useCallback((): void => {
    setDragForbiddenCursor(false);
    handleDragStart();
  }, [handleDragStart, setDragForbiddenCursor]);

  const handleSortableEnd = useCallback((): void => {
    setDragForbiddenCursor(false);
    handleDragEnd();
  }, [handleDragEnd, setDragForbiddenCursor]);

  // ===== 6) 列表项渲染：renderItem 透传 / 默认节点 =====
  const defaultRenderItem = useCallback(
    (item: TItem): React.ReactNode => (
      <>
        <Icon
          type="drop"
          className="dbl-two-drag-list-item-icon"
        />
        <span className="dbl-two-drag-list-item-text">{item.text}</span>
      </>
    ),
    [],
  );

  return (
    <div
      className={classNames(
        'dbl-two-drag-list-wrapper',
        containerClassName,
        { [DRAG_NOT_ALLOWED_CURSOR_CLASS]: isDragForbidden },
      )}
      style={containerStyle}
    >
      {/* ===== 7) 渲染：上下双框 + 中间连接图标 ===== */}
      <div
        className={classNames('dbl-two-drag-list-box', 'dbl-two-drag-list-box--top', topBoxClassName)}
        style={{ ...maxHeightStyle, ...topBoxStyle }}
        data-two-drag-side="top"
      >
        {topTitle ? (
          <div className="dbl-two-drag-list-title">{topTitle}</div>
        ) : null}

        <ReactSortable
          group="dbl-two-drag-list-group"
          list={topItems}
          setList={handleTopSetList}
          animation={animation}
          delay={delay}
          handle={handle}
          filter={mergedFilter}
          disabled={disabled}
          chosenClass={chosenClass}
          dragClass={dragClass}
          ghostClass={ghostClass}
          fallbackClass={fallbackClass}
          forceFallback={true}
          onMove={handleMove}
          onStart={handleSortableStart}
          onEnd={handleSortableEnd}
          className={classNames('dbl-two-drag-list-ul', 'dbl-two-drag-list-ul--top', {
            'dbl-two-drag-list-ul--empty': isEmpty(topItems),
          })}
        >
          {topItems.map((item, index) => (
            <div
              key={item.id}
              data-id={item.id}
              className={classNames(
                'dbl-two-drag-list-item',
                'dbl-two-drag-list-item--top',
                item.className,
                { [disabledItemClassName]: item.dragDisabled },
              )}
            >
              {renderItem
                ? renderItem({ item, index, side: 'top' })
                : defaultRenderItem(item)}
            </div>
          ))}
        </ReactSortable>
      </div>

      <div className="dbl-two-drag-list-middle-icon">
        <SvgIcon
          className="dbl-two-drag-list-middle-icon-refresh"
          type="refresh-icon-type"
        />
      </div>

      <div
        className={classNames('dbl-two-drag-list-box', 'dbl-two-drag-list-box--bottom', bottomBoxClassName)}
        style={{ ...maxHeightStyle, ...bottomBoxStyle }}
        data-two-drag-side="bottom"
      >
        {bottomTitle ? (
          <div className="dbl-two-drag-list-title">{bottomTitle}</div>
        ) : null}

        <ReactSortable
          group="dbl-two-drag-list-group"
          list={bottomItems}
          setList={handleBottomSetList}
          animation={animation}
          delay={delay}
          handle={handle}
          filter={mergedFilter}
          disabled={disabled}
          chosenClass={chosenClass}
          dragClass={dragClass}
          ghostClass={ghostClass}
          fallbackClass={fallbackClass}
          forceFallback={true}
          onMove={handleMove}
          onStart={handleSortableStart}
          onEnd={handleSortableEnd}
          className={classNames('dbl-two-drag-list-ul', 'dbl-two-drag-list-ul--bottom', {
            'dbl-two-drag-list-ul--empty': isEmpty(bottomItems),
          })}
        >
          {bottomItems.map((item, index) => (
            <div
              key={item.id}
              data-id={item.id}
              className={classNames(
                'dbl-two-drag-list-item',
                'dbl-two-drag-list-item--bottom',
                item.className,
                { [disabledItemClassName]: item.dragDisabled },
              )}
            >
              {renderItem
                ? renderItem({ item, index, side: 'bottom' })
                : defaultRenderItem(item)}
            </div>
          ))}
        </ReactSortable>
      </div>
    </div>
  );
}

const TwoDragListMemo = React.memo(TwoDragListBase);
TwoDragListMemo.displayName = 'TwoDragList';

export const TwoDragList = TwoDragListMemo as typeof TwoDragListBase;

export default TwoDragList;

